/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Grid from "@mui/material/Grid";
import summaryStyle from "../summaryStyle";
import GridEditRow from "../../Common/GridEditRow/GridEditRow";
import Typography from "@mui/material/Typography";
import {Box} from "@mui/material";

type CostsSummaryProps = {
  formData: Record<string, any>;
  basePath: string;
};

const CostsSummary = ({ formData, basePath }: CostsSummaryProps) => {
  return (
    <>
      <Grid item xs={12} sx={summaryStyle.subHeadline}>
        <Typography variant={"h5"}>Ausgaben</Typography>
      </Grid>
      <Grid item xs={12} sx={summaryStyle.question}>
        Abzüge
      </Grid>

      <Box id={"hasTaxDecuction"} sx={summaryStyle.scrollToAnchor} />
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "steuern",
          state: {
            field: "hasTaxDecuction",
          },
        }}
      >
        Steuern: {formData.hasTaxDecuction === "yes" ? formData.taxAmount + " €" : "keine Abzüge"}
      </GridEditRow>

      <Box id={"hasSocialSecurityDeduction"} sx={summaryStyle.scrollToAnchor} />
      <GridEditRow
        item
        xs={12}
        sx={summaryStyle.addMargin}
        to={{
          pathname: basePath + "sozialversicherung",
          state: {
            field: "hasSocialSecurityDeduction",
          },
        }}
      >
        Sozialversicherung: {formData.hasSocialSecurityDeduction === "yes" ? formData.socialSecurityAmount + " €" : "keine Abzüge"}
      </GridEditRow>

      <Box id={"hasOtherInsuranceDeduction"} sx={summaryStyle.scrollToAnchor} />
      <GridEditRow
        item
        xs={12}
        sx={summaryStyle.addMargin}
        to={{
          pathname: basePath + "versicherungen",
          state: {
            field: "hasOtherInsuranceDeduction",
          },
        }}
      >
        Sonstige Versicherungen: {formData.hasOtherInsuranceDeduction === "yes" ? formData.otherInsuranceAmount + " €" : "keine Abzüge"}
      </GridEditRow>

      <Box id={"hasMobilityCostDeduction"} sx={summaryStyle.scrollToAnchor} />
      <GridEditRow
        item
        xs={12}
        sx={summaryStyle.addMargin}
        to={{
          pathname: basePath + "fahrtkosten",
          state: {
            field: "hasMobilityCostDeduction",
          },
        }}
      >
        Fahrt zur Arbeit: {formData.hasMobilityCostDeduction === "yes" ? formData.mobilityCostAmount + " " + formData.mobilityCostUnit : "keine Abzüge"}
      </GridEditRow>

      <Box id={"hasOtherExpensesDeduction"} sx={summaryStyle.scrollToAnchor} />
      <GridEditRow
        item
        xs={12}
        sx={summaryStyle.addMargin}
        to={{
          pathname: basePath + "werbungskosten",
          state: {
            field: "hasOtherExpensesDeduction",
          },
        }}
      >
        Werbungskosten: {formData.hasOtherExpensesDeduction === "yes" ? formData.otherExpensesAmount + " €" : "keine Abzüge"}
      </GridEditRow>

      {formData.hasIncomeLiving === "no" && (
        <>
          <Grid item xs={12} sx={summaryStyle.question}>
            Wohnkosten
          </Grid>
          <GridEditRow
            item
            xs={12}
            to={{
              pathname: basePath + "wohnkosten",
              state: {
                field: "livingType",
              },
            }}
          >
            {formData.livingType === "rent" ? "Ich wohne zur Miete" : "Ich bin Eigentümer"}
            <br />
            Größe des Wohnraums: {formData.appartmentSize} m², {formData.numberRooms} Zimmer
            <br />
            {formData.numberRoommates === 0 && "Ich lebe alleine"}
            {formData.numberRoommates === 1 && "Ich lebe mit einer Person zusammen"}
            {formData.numberRoommates > 1 && "Ich lebe mit " + formData.numberRoommates + " Personen zusammen"}
            <br />
            {formData.livingType === "rent" ? "Kaltmiete: " : "Zinsen und Tilgung: "}
            {formData.basicRentOrRepayment} €<br />
            Heizungskosten: {formData.heatingCosts}
            <br />
            Nebenkosten: {formData.additionalCosts}
            <br />
            Ich zahle davon {formData.costPaidByPetitioner} €<br />
            <br />
          </GridEditRow>

          {formData.livingType !== "rent" && (
            <>
              <Grid item xs={12} sx={summaryStyle.question}>
                <Box id={"hasPropertyLoans"} sx={summaryStyle.scrollToAnchor} />
                Immobilienkredite
              </Grid>
              <GridEditRow
                item
                xs={12}
                to={{
                  pathname: basePath + "wohnkosten",
                  state: {
                    field: "hasPropertyLoans",
                  },
                }}
              >
                {formData.hasPropertyLoans === "no" ? (
                  "Keine Immobilienkredite"
                ) : (
                  <>
                    {formData.propertyLoans?.map((propertyLoan: Record<string, any>, key: number) => (
                      <React.Fragment key={key}>
                        Kredit bei {propertyLoan.bank} seit {propertyLoan.date}
                        <br />
                        Darlehensnehmer: {propertyLoan.borrowers}
                        <br />
                        Restschuld von {propertyLoan.outstandingDebt}, zu zahlen mit {propertyLoan.repayment} €/Monat bis {propertyLoan.remainingTerm}
                        <br />
                        <br />
                      </React.Fragment>
                    ))}
                  </>
                )}
              </GridEditRow>
            </>
          )}
        </>
      )}

      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasOtherPaymentObligations"} sx={summaryStyle.scrollToAnchor} />
        Sonstige Zahlungsverpflichtungen
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "zahlungsverpflichtungen",
          state: {
            field: "hasOtherPaymentObligations",
          },
        }}
      >
        {formData.hasOtherPaymentObligations === "no" ? (
          "Keine sonstigen Zahlungsverpflichtungen"
        ) : (
          <>
            {formData.paymentObligations?.map((paymentObligation: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                {paymentObligation.name}
                <br />
                Restschuld von {paymentObligation.outstandingDebt} €, zu zahlen mit {paymentObligation.repayment} €/Monat (davon{" "}
                {paymentObligation.petitionerShare} € durch mich) bis {paymentObligation.remainingTerm}
                <br />
                <br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>

      <Grid item xs={12} sx={summaryStyle.question}>
        <Box id={"hasSpecialFinancialBurdens"} sx={summaryStyle.scrollToAnchor} />
        Besondere Belastungen
      </Grid>
      <GridEditRow
        item
        xs={12}
        to={{
          pathname: basePath + "besondere-belastungen",
          state: {
            field: "hasSpecialFinancialBurdens",
          },
        }}
      >
        {formData.hasSpecialFinancialBurdens === "no" ? (
          "Keine besonderen Belastungen"
        ) : (
          <>
            {formData.specialFinancialBurdens?.map((specialFinancialBurden: Record<string, any>, key: number) => (
              <React.Fragment key={key}>
                {specialFinancialBurden.description}
                <br />
                Ich zahle davon {specialFinancialBurden.petitionerShare} €
                <br />
                <br />
              </React.Fragment>
            ))}
          </>
        )}
      </GridEditRow>
    </>
  );
};

export default CostsSummary;
